import { useEffect } from 'react'
import { useSelector } from 'react-redux'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import LazyLoad from 'react-lazyload'
import InfiniteContainer from 'components/common/InfiniteContainer'
import styled from '@emotion/styled'

import {
  useArticleLayout,
  useArticleClassifications,
  useArticles,
} from 'utils/api/hooks'
import { selectWebview } from 'store/webview/webviewSlice'
import {
  enableNativeHeaderMainTabs,
  enableNativeHeaderNavBack,
  enableNativeHeaderSearch,
} from 'utils/native-notifier'
import Layout from 'components/common/Layout'
import Header from 'components/common/Header'
import Footer from 'components/common/Footer'
import ContentWrapper from 'components/common/ContentWrapper'
import GutterGrid from 'components/common/GutterGrid'
import { HideInMobileGrid } from 'components/common/Hidden'
import ArticleClassificationSwiper from './ArticleClassificationSwiper'
import ArticleCard from './ArticleCard'

const MainGrid = styled(GutterGrid)`
  max-width: none;
`

const FirstGrid = styled(Grid)`
  order: 1;
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      order: 2;
    }
  `}
`

const SecondGrid = styled(Grid)`
  order: 2;
  ${({ theme }) => `
    ${theme.breakpoints.up('lg')} {
      order: 1;
    }
  `}
`

const ThirdGrid = styled(Grid)`
  order: 3;
`

const ArticleIndex = ({ fallbackData }) => {
  const {
    data: layout,
    isLoading: isLayoutLoading,
  } = useArticleLayout('mainArticles', { fallbackData: [fallbackData?.layout] })
  const { data: alternateArticles } = useArticles(
    () => {
      return !isLayoutLoading && layout?.mainArticles?.length < 6
        ? { limit: 6 - (layout?.mainArticles?.length || 0) }
        : null
    },
    { fallbackData: fallbackData?.alternateArticles }
  )
  const specialArticles = layout?.mainArticles.concat(alternateArticles || [])

  const { data: categories } = useArticleClassifications(null, {
    fallbackData: fallbackData?.articleClassifications,
  })

  const firstGroupOfCategories = (categories || []).slice(0, 2)
  const restOfCategories = (categories || []).slice(2)

  const webview = useSelector(selectWebview)
  useEffect(() => {
    if (webview?.name === 'lsApp') {
      try {
        enableNativeHeaderMainTabs(true)
        enableNativeHeaderNavBack(false)
        enableNativeHeaderSearch(true)
      } catch (err) {
        console.error('native notifier error: ', err.message)
      }
    }
  }, [webview])

  const containerId = 'article-homepage-scrollable'

  return (
    <Layout stickyHeader={<Header />} fixContentHeight>
      <InfiniteContainer id={containerId}>
        <ContentWrapper mt={{ xs: 3, lg: 12 }} pb={10}>
          <MainGrid xs={false} md={false} lg={false}>
            <FirstGrid item xs={12} lg={6}>
              <ArticleCard article={specialArticles?.[0]} main />
            </FirstGrid>
            <SecondGrid item xs={12} lg={3}>
              <Box mt={{ xs: 14, lg: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} lg={12}>
                    <ArticleCard article={specialArticles?.[1]} />
                  </Grid>
                  <Grid item xs={6} lg={12}>
                    <Box mt={{ xs: 0, lg: 12 }}>
                      <ArticleCard article={specialArticles?.[2]} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </SecondGrid>
            <ThirdGrid item xs={12} lg={3}>
              <Box mt={{ xs: 14, lg: 0 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6} lg={12}>
                    <ArticleCard article={specialArticles?.[3]} />
                  </Grid>
                  <Grid item xs={6} lg={12}>
                    <Box mt={{ xs: 0, lg: 12 }}>
                      <ArticleCard article={specialArticles?.[4]} />
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </ThirdGrid>
          </MainGrid>
        </ContentWrapper>

        {firstGroupOfCategories?.map((category) => (
          <ArticleClassificationSwiper
            key={category._id}
            classification={category}
          />
        ))}

        <ContentWrapper mt={{ xs: 16, lg: 24 }}>
          <Grid container>
            <HideInMobileGrid item lg={1} />
            <Grid item xs={12} lg={10}>
              <ArticleCard article={specialArticles?.[5]} sixth />
            </Grid>
          </Grid>
        </ContentWrapper>

        {restOfCategories?.map((category) => (
          <LazyLoad
            height={390}
            offset={200}
            key={category._id}
            scrollContainer={`#${containerId}`}
          >
            <ArticleClassificationSwiper classification={category} />
          </LazyLoad>
        ))}

        <Box mt={16} />

        {webview?.name !== 'lsApp' && <Footer />}
      </InfiniteContainer>
    </Layout>
  )
}

export default ArticleIndex
